import { Component } from "@theme/component";
import {
  fetchConfig,
  preloadImage,
  onAnimationEnd,
  yieldToMainThread,
} from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  CartUpdateEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { sectionRenderer } from "@theme/section-renderer";
import { morph } from "@theme/morph";

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * Warms the cart-drawer section render endpoint on the user's first ATC
 * intent (pointerenter / focus on an add-to-cart button). One-shot per page.
 *
 * The fetched HTML is discarded — by the time the user clicks, cart state
 * has changed, so the real post-add fetch passes `cache: false` and re-runs.
 * The win is HTTP keep-alive being warm and Shopify's server-side Liquid
 * template cache being primed.
 */
let cartDrawerSectionWarmed = false;
function warmCartDrawerSection() {
  if (cartDrawerSectionWarmed) return;
  cartDrawerSectionWarmed = true;

  const drawer = document.querySelector("cart-drawer-component[auto-open]");
  const section = drawer?.closest(".shopify-section");
  if (!section?.id) return;

  const sectionId = section.id.replace(/^shopify-section-/, "");
  // Fire and forget; sectionRenderer handles caching + abort coordination.
  sectionRenderer.getSectionHTML(sectionId).catch(() => {});
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  /** @type {number | null} */
  #feedbackMatrixFrame = null;

  /** @type {HTMLElement | null} */
  #feedbackMatrixElement = null;

  /** @type {string | null} */
  #feedbackMatrixOriginalText = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(
        /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
      );
    }

    this.#stopFeedbackMatrixAnimation();

    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Toggles the in-flight loading state. The CSS rule keyed off
   * `data-loading="true"` provides the visual indication; `aria-busy` keeps
   * assistive tech in sync.
   * @param {boolean} loading
   */
  setLoading(loading) {
    const { addToCartButton } = this.refs;
    if (loading) {
      addToCartButton.dataset.loading = "true";
      addToCartButton.setAttribute("aria-busy", "true");
    } else {
      delete addToCartButton.dataset.loading;
      addToCartButton.removeAttribute("aria-busy");
    }
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest("form");
    if (!form?.checkValidity()) return;

    // Don't replay the optimistic animation while a previous add is still in flight.
    if (this.refs.addToCartButton.dataset.loading === "true") return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (
      this.closest("product-form-component")
    );
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    if (this.refs.addToCartButton.dataset.puppet !== "true") {
      const animationEnabled = this.dataset.addToCartAnimation === "true";
      const successAnimationEnabled = this.dataset.successAnimation !== "false";

      if (!animationEnabled && !successAnimationEnabled) {
        this.#stopFeedbackMatrixAnimation();
        this.refs.addToCartButton.removeAttribute("data-text-feedback");
        return;
      }

      if (successAnimationEnabled) {
        this.animateAddToCart();
      } else {
        this.showTextFeedback();
      }
    }

    // Keep submission deterministic across mobile browsers (especially when
    // tapping nested text/icon nodes inside the submit button): submit
    // programmatically and suppress the native click submission path.
    if (form instanceof HTMLFormElement && typeof form.requestSubmit === "function") {
      event.preventDefault();
      form.requestSubmit(this.refs.addToCartButton);
    }
  }

  #preloadImage = () => {
    // Warm the cart-drawer section render endpoint on first user intent:
    // primes HTTP keep-alive + Shopify's Liquid template cache so the real
    // post-add fetch lands faster. Cheap (one-shot, ignores result).
    warmCartDrawerSection();

    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    let flyToCartClass = addToCartButton.classList.contains("quick-add__button")
      ? "fly-to-cart--quick"
      : "fly-to-cart--main";

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.style.setProperty("--start-opacity", "0");
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
    );
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== "true") {
      addToCartButton.dataset.added = "true";
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute("data-added");

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };

  showTextFeedback = () => {
    const { addToCartButton } = this.refs;
    const addedMessage = /** @type {HTMLElement | null} */ (
      addToCartButton.querySelector(".add-to-cart-text__added-message")
    );

    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    this.#resetTimeouts.forEach(
      /** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId),
    );
    this.#resetTimeouts = [];

    this.#suspendOriginalMatrixHover(addToCartButton);

    if (this.dataset.matrixFeedback === "true" && addedMessage) {
      this.#startFeedbackMatrixAnimation(
        addedMessage,
        this.#getAddedToCartText(),
      );
    } else {
      this.#stopFeedbackMatrixAnimation();
    }

    addToCartButton.dataset.textFeedback = "true";

    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute("data-text-feedback");
      this.#stopFeedbackMatrixAnimation();
      const restoredMatrixTarget =
        this.#restoreOriginalMatrixHover(addToCartButton);

      if (this.dataset.matrixFeedback === "true" && restoredMatrixTarget) {
        this.#startFeedbackMatrixAnimation(
          restoredMatrixTarget,
          this.#getOriginalAddToCartText(),
        );
      }

      if (!this.#resetTimeouts) return;

      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 1200);

    this.#resetTimeouts.push(timeoutId);
  };

  /**
   * @param {HTMLButtonElement} addToCartButton
   */
  #suspendOriginalMatrixHover(addToCartButton) {
    const matrixTarget = /** @type {HTMLElement | null} */ (
      addToCartButton.querySelector(
        '.add-to-cart-text__content [data-matrix-text], .add-to-cart-text__content [data-matrix-restore-on-leave="true"]',
      )
    );

    if (!matrixTarget) return;

    const canonicalText =
      this.#getOriginalAddToCartText() ??
      matrixTarget.dataset.matrixOriginalText ??
      matrixTarget.textContent ??
      "";
    matrixTarget.textContent = canonicalText;

    const clone = /** @type {HTMLElement} */ (matrixTarget.cloneNode(true));
    clone.removeAttribute("data-matrix-text");
    clone.removeAttribute("data-matrix-restore-on-leave");
    clone.dataset.matrixSuspended = "true";
    clone.textContent = canonicalText;
    clone.dataset.matrixOriginalText = canonicalText;

    matrixTarget.replaceWith(clone);
  }

  /**
   * @param {HTMLButtonElement} addToCartButton
   * @returns {HTMLElement | null}
   */
  #restoreOriginalMatrixHover(addToCartButton) {
    const suspendedTarget = /** @type {HTMLElement | null} */ (
      addToCartButton.querySelector(
        '.add-to-cart-text__content [data-matrix-suspended="true"]',
      )
    );

    if (!suspendedTarget) return null;

    const canonicalText =
      this.#getOriginalAddToCartText() ??
      suspendedTarget.dataset.matrixOriginalText ??
      suspendedTarget.textContent ??
      "";
    suspendedTarget.textContent = canonicalText;

    suspendedTarget.removeAttribute("data-matrix-suspended");
    suspendedTarget.dataset.matrixOriginalText = canonicalText;

    if (addToCartButton.matches(":hover")) {
      suspendedTarget.dataset.matrixRestoreOnLeave = "true";
      addToCartButton.addEventListener(
        "pointerleave",
        this.#restoreMatrixOnLeave,
        { once: true },
      );
      return null;
    }

    suspendedTarget.setAttribute("data-matrix-text", "");

    return suspendedTarget;
  }

  /**
   * @param {PointerEvent} event
   */
  #restoreMatrixOnLeave = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const pendingTarget = /** @type {HTMLElement | null} */ (
      button.querySelector(
        '.add-to-cart-text__content [data-matrix-restore-on-leave="true"]',
      )
    );

    if (!pendingTarget) return;

    const canonicalText =
      this.#getOriginalAddToCartText() ??
      pendingTarget.dataset.matrixOriginalText ??
      pendingTarget.textContent;
    pendingTarget.textContent = canonicalText;
    pendingTarget.dataset.matrixOriginalText = canonicalText;
    pendingTarget.removeAttribute("data-matrix-restore-on-leave");
    pendingTarget.setAttribute("data-matrix-text", "");
  };

  /**
   * @param {HTMLElement} element
   * @param {string | null} [preferredText]
   */
  #startFeedbackMatrixAnimation(element, preferredText = null) {
    this.#stopFeedbackMatrixAnimation();

    const originalText =
      preferredText ??
      element.dataset.matrixOriginalText ??
      element.textContent ??
      "";
    if (!originalText.trim()) return;

    element.textContent = originalText;
    element.dataset.matrixOriginalText = originalText;

    this.#feedbackMatrixElement = element;
    this.#feedbackMatrixOriginalText = originalText;

    const characterSet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    const duration = 360;
    const startTime = performance.now();

    /** @param {DOMHighResTimeStamp} currentTime */
    const tick = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const revealCount = Math.floor(progress * originalText.length);

      let output = "";
      for (let index = 0; index < originalText.length; index += 1) {
        const character = originalText.charAt(index);

        if (/\s/.test(character) || index < revealCount) {
          output += character;
          continue;
        }

        const randomIndex = Math.floor(Math.random() * characterSet.length);
        output += characterSet[randomIndex] || character;
      }

      element.textContent = output;

      if (progress >= 1) {
        element.textContent = originalText;
        this.#feedbackMatrixFrame = null;
        this.#feedbackMatrixElement = null;
        this.#feedbackMatrixOriginalText = null;
        return;
      }

      this.#feedbackMatrixFrame = requestAnimationFrame(tick);
    };

    this.#feedbackMatrixFrame = requestAnimationFrame(tick);
  }

  #stopFeedbackMatrixAnimation() {
    if (this.#feedbackMatrixFrame !== null) {
      cancelAnimationFrame(this.#feedbackMatrixFrame);
      this.#feedbackMatrixFrame = null;
    }

    if (
      this.#feedbackMatrixElement &&
      this.#feedbackMatrixOriginalText !== null
    ) {
      this.#feedbackMatrixElement.textContent =
        this.#feedbackMatrixOriginalText;
      this.#feedbackMatrixElement.dataset.matrixOriginalText =
        this.#feedbackMatrixOriginalText;
    }

    this.#feedbackMatrixElement = null;
    this.#feedbackMatrixOriginalText = null;
  }

  #getOriginalAddToCartText() {
    return this.dataset.originalAddToCartText ?? null;
  }

  #getAddedToCartText() {
    return this.dataset.addedToCartText ?? null;
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal },
    );

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, {
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find(
      (item) => item.variant_id.toString() === variantIdInput.value.toString(),
    );
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (
      this.querySelector("quantity-selector-component")
    );
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    );
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();

      // Reconcile the header cart-bubble with the authoritative cart count.
      // The cart-drawer lives in its own section (926c0fb), so the section
      // morph after add only refreshes the drawer body — not the header.
      // Without this, cart-icon.renderCartBubble's optimistic
      // `currentCount + itemCount` increment never gets corrected and the
      // bubble drifts (e.g. cart cleared elsewhere -> bubble stuck on old N).
      this.#reconcileCartBubble(cart?.item_count);

      return this.#updateCartQuantityFromData(cart);
    } catch (error) {
      console.error("Failed to fetch cart quantity:", error);
      return 0;
    }
  }

  /**
   * Resets every cart-bubble in the document to the authoritative count.
   *
   * The theme renders the `cart-bubble` snippet in multiple places (the icon
   * overlay, the "Cart" text label, the drawer heading). Only the bubble
   * inside `<cart-icon>` is wrapped by a component that listens to
   * cart:update — the others have no JS keeping them current. Before
   * 926c0fb the section morph kept them all in sync; now we have to do it
   * here.
   *
   * @param {number | undefined} itemCount
   */
  #reconcileCartBubble(itemCount) {
    if (typeof itemCount !== "number") return;

    const displayText = itemCount < 100 ? String(itemCount) : "99+";
    const isEmpty = itemCount === 0;

    for (const el of document.querySelectorAll(".cart-bubble__text-count")) {
      el.textContent = displayText;
      el.classList.toggle("hidden", isEmpty);
    }

    for (const el of document.querySelectorAll(".cart-bubble")) {
      el.classList.toggle("visually-hidden", isEmpty);
    }

    for (const el of document.querySelectorAll("cart-icon")) {
      el.classList.toggle("header-actions__cart-icon--has-cart", !isEmpty);
      // Keep cart-icon's own optimistic counter in sync with the truth.
      const cartIcon = /** @type {any} */ (el);
      if (typeof cartIcon.renderCartBubble === "function") {
        cartIcon.renderCartBubble(itemCount, false, false);
      }
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (
      event.detail?.sourceId === this.id ||
      event.detail?.data?.source === "product-form-component"
    )
      return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Query for ALL add-to-cart components
    const allAddToCartContainers =
      /** @type {NodeListOf<AddToCartComponent>} */ (
        this.querySelectorAll("add-to-cart-component")
      );

    // Bail if any button is disabled or already loading. The loading check is
    // important for keyboard re-submits (Enter on a focused button) since
    // CSS `pointer-events: none` only blocks mouse interaction.
    const anyButtonBlocked = Array.from(allAddToCartContainers).some(
      (container) => {
        const btn = container.refs.addToCartButton;
        return !btn || btn.disabled || btn.dataset.loading === "true";
      },
    );
    if (anyButtonBlocked) return;

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    if (this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        // Disable ALL add-to-cart buttons
        for (const container of allAddToCartContainers) {
          container.disable();
        }

        const errorTemplate = this.dataset.quantityErrorMax || "";
        const errorMessage = errorTemplate.replace(
          "{{ maximum }}",
          validation.maxQuantity?.toString() || "",
        );
        if (addToCartTextError) {
          addToCartTextError.classList.remove("hidden");

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          // Re-enable ALL add-to-cart buttons
          for (const container of allAddToCartContainers) {
            container.enable();
          }
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    // Request the cart-drawer section inline on /cart/add.js. The drawer
    // itself opens optimistically at click time via #openCartDrawerOptimistic
    // (instant, CSS-only pending state), so the only thing the user is
    // waiting on is the line item rendering. Asking for the section here
    // returns the rendered HTML in the same round trip — measured ~1.1s
    // total — vs. omitting sections and letting component-cart-items fall
    // back to a second GET /?section_id=... request, which benchmarks at
    // ~2.0s total (faster add response, but two serial round trips).
    const drawerCartItems = document.querySelector(
      "cart-items-component[data-drawer]",
    );
    const drawerSectionId =
      drawerCartItems instanceof HTMLElement
        ? drawerCartItems.dataset.sectionId
        : null;
    if (drawerSectionId) {
      formData.set("sections", drawerSectionId);
    } else {
      formData.delete("sections");
      formData.delete("section-id");
    }

    // Open the cart drawer immediately at click time and mark it pending so
    // the CSS-only progress bar at the top of the dialog animates while
    // /cart/add.js is in flight. The drawer's existing items dim until the
    // cart-items section actually morphs in (cart-drawer.js#handleCartAdd
    // watches the cart-items subtree with a MutationObserver and clears
    // pending only after that morph lands).
    const optimisticDrawer = this.#openCartDrawerOptimistic();

    const fetchCfg = fetchConfig("javascript", { body: formData });

    // Show loading state until the drawer opens (success) or an error shows.
    for (const container of allAddToCartContainers) {
      container.setLoading(true);
    }

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "application/json",
      },
      // Prioritise this request over background work (image preloads,
      // prefetches, analytics) so the drawer opens as soon as possible.
      // Ignored by browsers that don't support fetch priority hints.
      priority: "high",
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          // Roll back the optimistic skeleton so the drawer doesn't keep a
          // pending row that will never resolve.
          optimisticDrawer?.cancelOptimistic();

          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute("id") || "",
              response.message,
              response.description,
              response.errors,
            ),
          );

          if (!addToCartTextError) return;
          this.#showAddErrorMessage(addToCartTextError, response.message);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            }),
          );

          return;
        } else {
          const id = formData.get("id");

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          if (!id) throw new Error("Form ID is required");

          // Add aria-live region to inform screen readers that the item was added
          // Get the added text from any add-to-cart button
          const anyAddToCartButton =
            allAddToCartContainers[0]?.refs.addToCartButton;
          if (anyAddToCartButton) {
            const addedTextElement = anyAddToCartButton.querySelector(
              ".add-to-cart-text--added",
            );
            const addedText =
              addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Dispatch the cart-add event immediately so the drawer's
          // section morph runs in the same tick as the response. Updating the
          // PDP quantity selector with the per-variant total is non-critical
          // visual sync and runs in the background — it must NOT block the
          // cart drawer paint, which previously added an extra round-trip
          // (~200-500ms) before the drawer ever showed the new line item.
          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            }),
          );

          // With the cart set to a page there is no drawer to show the result in,
          // so send the customer to the cart the way a non-JS form post would.
          if (Theme.cart?.type === "page") {
            window.location.assign(Theme.routes.cart_url);
            return;
          }

          // Refresh the PDP quantity selector in the background; the drawer
          // doesn't need this to open, so we don't await it.
          this.#fetchAndUpdateCartQuantity();
        }
      })
      .catch((error) => {
        // Network failure: roll back the optimistic skeleton.
        optimisticDrawer?.cancelOptimistic();
        console.error(error);

        // Surface a visible error so the user isn't left staring at a button
        // that just snapped back with no explanation (e.g. offline, DNS, CORS).
        if (!addToCartTextError) return;
        const message =
          Theme.translations.add_to_cart_error || "Error adding to cart";
        this.#showAddErrorMessage(addToCartTextError, message);
      })
      .finally(() => {
        for (const container of allAddToCartContainers) {
          container.setLoading(false);
        }
      });
  }

  /**
   * Opens the auto-open cart drawer immediately so the user gets feedback
   * before the network round-trip resolves. The drawer self-styles a pending
   * state via the `data-pending-add` attribute (CSS only, no DOM injected).
   *
   * Returns the drawer element so the caller can roll the optimistic state
   * back on error. Returns null if there is no `auto-open` drawer.
   *
   * @returns {(HTMLElement & { cancelOptimistic: () => void }) | null}
   */
  #openCartDrawerOptimistic() {
    const drawer = /** @type {any} */ (
      document.querySelector("cart-drawer-component[auto-open]")
    );
    if (!drawer || typeof drawer.openOptimistic !== "function") return null;

    // Pass through the variant image (already on the AddToCart button via
    // `data-product-variant-media`) and product title so the drawer can
    // render an optimistic line-item skeleton immediately, before /cart/add.js
    // returns. variantId enables duplicate detection in the drawer (when the
    // variant is already in cart, the morph will just bump quantity).
    const addToCartButton = /** @type {HTMLElement | null} */ (
      this.querySelector("add-to-cart-component, .quick-add__button")
    );
    const image = addToCartButton?.dataset.productVariantMedia;
    const title = this.dataset.productTitle;
    const productUrl = this.dataset.productUrl;
    const variantId =
      /** @type {HTMLInputElement | null} */ (
        this.querySelector('input[name="id"]')
      )?.value;

    // Scrape the currently-displayed variant price from the nearest
    // <product-price> in the same product context. This is what the user is
    // looking at, so it matches what they expect to see in the optimistic
    // row. Format is already localized (e.g. "$220.00").
    const priceText = this.#scrapeVariantPriceText();

    // Scrape selected variant options (Color, Size, ...) from the variant
    // picker so the optimistic row shows the right metadata. Best-effort:
    // works for radio + select variant pickers, ignored otherwise.
    const options = this.#scrapeVariantOptions();

    drawer.openOptimistic({
      image,
      title,
      variantId,
      productUrl,
      priceFormatted: priceText,
      options,
    });
    return drawer;
  }

  /**
   * Returns the currently-displayed price text (localized money string) for
   * this product, or undefined if not found. Looks at the nearest
   * `<product-price>` in this form's product card / page section.
   *
   * @returns {string | undefined}
   */
  #scrapeVariantPriceText() {
    // Prefer a price element inside the same product card / section.
    // Walk up until we find a container with a product-price descendant.
    /** @type {Element | null} */
    let scope = this;
    while (scope && scope !== document.body) {
      const price = scope.querySelector?.(
        "product-price, .price__regular, .price__sale",
      );
      if (price) {
        // `snippets/price.liquid` renders the active price as `.price` and
        // the strike-through value as `.compare-at-price`, alongside
        // visually-hidden labels ("Regular price", "Sale price"). We must
        // target the single active-price node — never the parent's
        // `textContent` — or we'll concatenate "$300.00$220.00" plus
        // labels and parse a nonsense number that pegs the free-shipping
        // bar at 100%.
        const sale =
          price.querySelector?.(".price__sale .price-item--sale") ||
          price.querySelector?.(".price-item--sale");
        const regular =
          price.querySelector?.(".price-item--regular") ||
          price.querySelector?.(".price-item");
        // Active price node (theme convention): `.price` inside a sale
        // wrapper is the discounted price; `.compare-at-price` is the
        // original. Prefer `.price`, explicitly avoid `.compare-at-price`.
        const active = price.querySelector?.(".price");
        const target = sale || active || regular;
        if (target) {
          const text = target.textContent?.replace(/\s+/g, " ").trim();
          if (text) return text;
        }
        // Last-resort fallback: scan the container's text but drop any
        // visually-hidden labels and the compare-at-price first.
        const clone = /** @type {Element} */ (price.cloneNode(true));
        clone
          .querySelectorAll(".visually-hidden, .compare-at-price")
          .forEach((n) => n.remove());
        const text = clone.textContent?.replace(/\s+/g, " ").trim();
        if (text) return text;
      }
      scope = scope.parentElement;
    }
    return undefined;
  }

  /**
   * Returns the currently-selected variant options as `[{name, value}]`.
   * Reads radio fieldsets (variant-main-picker) and selects within any
   * variant-picker descendant of this form. Skips swatches-only secondary
   * pickers (their checked state mirrors the main picker).
   *
   * @returns {Array<{name: string, value: string}>}
   */
  #scrapeVariantOptions() {
    /** @type {Array<{name: string, value: string}>} */
    const options = [];

    // Preferred path: read the authoritative server-rendered variant map
    // embedded in buy-buttons.liquid. This sidesteps every DOM quirk
    // (swatch pickers with no `:checked` on first load when there's no
    // `?variant=` in the URL, Color hidden from `<variant-picker>` on
    // combined-listing PDPs, dropdown-only pickers, etc.) by mapping the
    // form's current variant id straight to its ordered option values and
    // pairing them with the product's option names.
    const variantId = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="id"]')
    )?.value;
    if (variantId) {
      try {
        const variantsScript = this.querySelector(
          "script[data-product-variants]",
        );
        const namesScript = this.querySelector(
          "script[data-product-option-names]",
        );
        if (variantsScript?.textContent && namesScript?.textContent) {
          /** @type {Array<{id: number | string, options: string[]}>} */
          const variants = JSON.parse(variantsScript.textContent);
          /** @type {string[]} */
          const names = JSON.parse(namesScript.textContent);
          const match = variants.find(
            (v) => String(v.id) === String(variantId),
          );
          if (match && Array.isArray(match.options)) {
            match.options.forEach((value, i) => {
              const name = names[i];
              if (name && value) options.push({ name, value });
            });
            if (options.length) return options;
          }
        }
      } catch {
        // Fall through to DOM scraping below.
      }
    }

    // Fallback: DOM scrape. Collect *all* pickers matching this product
    // across the entire document, because on PDPs the swatch picker
    // (`<swatches-variant-picker-component>`) can live in a sibling block
    // (e.g. inside the media gallery or a standalone swatches block) that
    // isn't a descendant of the nearest common ancestor between this form
    // and the variant-picker. Filter by `data-product-id` so quick-add
    // modals or other product cards on the page don't bleed in.
    const productId = this.dataset.productId || "";
    /** @type {Element[]} */
    const allPickers = Array.from(
      document.querySelectorAll(
        "variant-picker, swatches-variant-picker-component",
      ),
    );
    /** @type {Element[]} */
    let pickers = productId
      ? allPickers.filter((p) => {
          const pid = /** @type {HTMLElement} */ (p).dataset?.productId;
          return !pid || pid === productId;
        })
      : allPickers;

    // Safety net: if filtering left nothing, fall back to the nearest
    // ancestor's pickers (older walk-up behavior).
    if (!pickers.length) {
      /** @type {Element | null} */
      let scope = this;
      while (scope && scope !== document.body) {
        const found = scope.querySelectorAll?.(
          "variant-picker, swatches-variant-picker-component",
        );
        if (found && found.length) {
          pickers = Array.from(found);
          break;
        }
        scope = scope.parentElement;
      }
    }
    if (!pickers.length) return options;

    /**
     * Pushes an option if both a name and value are present and the name
     * isn't already collected (selects can duplicate radio fieldsets for the
     * same option on combined-listing products).
     *
     * @param {string} name
     * @param {string | null | undefined} value
     */
    const pushOption = (name, value) => {
      const trimmedName = (name || "").trim();
      const trimmedValue = (value || "").trim();
      if (!trimmedName || !trimmedValue) return;
      if (options.some((o) => o.name.toLowerCase() === trimmedName.toLowerCase())) return;
      options.push({ name: trimmedName, value: trimmedValue });
    };

    pickers.forEach((picker) => {
      // Radio fieldsets (variant-main-picker buttons & swatches, plus
      // standalone variant-swatches.liquid which omits the `<legend>`).
      picker.querySelectorAll("fieldset.variant-option").forEach((fieldset) => {
        const legend = fieldset.querySelector("legend");
        let name = legend
          ? Array.from(legend.childNodes)
              .filter((n) => n.nodeType === Node.TEXT_NODE)
              .map((n) => n.textContent || "")
              .join("")
              .trim()
          : "";

        const checked = /** @type {HTMLInputElement | null} */ (
          fieldset.querySelector('input[type="radio"]:checked')
        );

        // No legend (e.g. swatches-variant-picker-component fieldsets in
        // variant-swatches.liquid) → derive the name from the radio's
        // `name` attribute, which is `"{OptionName}-{productId}[-swatch]"`.
        // Strip the trailing `-{digits}` and optional `-swatch` suffix.
        if (!name) {
          const radio = checked ?? fieldset.querySelector('input[type="radio"]');
          const rawName = /** @type {HTMLInputElement | null} */ (radio)?.name || "";
          const m = /^(.+?)-\d+(?:-swatch)?$/.exec(rawName);
          if (m && m[1]) name = m[1];
        }

        pushOption(name, checked?.value);
      });

      // Native selects (dropdown variant style).
      picker.querySelectorAll("select.variant-option__select").forEach((sel) => {
        const select = /** @type {HTMLSelectElement} */ (sel);
        const m = /^options\[(.+)\]$/.exec(select.name || "");
        pushOption(m?.[1] ?? "", select.value);
      });
    });

    return options;
  }

  /**
   * Shows an inline error message under the add-to-cart button and announces
   * it via the live region. Auto-hides after ERROR_MESSAGE_DISPLAY_DURATION.
   *
   * @param {HTMLElement} addToCartTextError - The error container element.
   * @param {string} message - The error message to display.
   */
  #showAddErrorMessage(addToCartTextError, message) {
    addToCartTextError.classList.remove("hidden");

    // Reuse the text node if the user is spam-clicking
    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = message;
    } else {
      addToCartTextError.appendChild(document.createTextNode(message));
    }

    this.#setLiveRegionText(message);

    if (this.#timeout) clearTimeout(this.#timeout);
    this.#timeout = setTimeout(() => {
      addToCartTextError.classList.add("hidden");
      this.#clearLiveRegionText();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle("hidden", cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * Applies immediate local button state updates while server-rendered variant HTML is loading.
   * @param {boolean} isAvailable
   * @param {string} variantId
   */
  #applyOptimisticVariantState(isAvailable, variantId) {
    const { addToCartButtonContainer } = this.refs;
    const addToCartButton = addToCartButtonContainer?.refs.addToCartButton;

    if (addToCartButton) {
      addToCartButton.disabled = !isAvailable;

      const addToCartIcon = addToCartButton.querySelector(".add-to-cart-icon");
      if (addToCartIcon instanceof HTMLElement) {
        addToCartIcon.classList.toggle(
          "add-to-cart-icon--hidden",
          !isAvailable,
        );
      }

      const labelElement = /** @type {HTMLElement | null} */ (
        addToCartButton.querySelector(
          ".add-to-cart-text__content [data-matrix-original-text]",
        )
      );

      if (labelElement) {
        const nextText = isAvailable
          ? addToCartButtonContainer?.dataset.addToCartText ||
            addToCartButtonContainer?.dataset.originalAddToCartText ||
            labelElement.textContent ||
            ""
          : addToCartButtonContainer?.dataset.soldOutText ||
            addToCartButtonContainer?.dataset.unavailableText ||
            labelElement.textContent ||
            "";

        labelElement.textContent = nextText;
        labelElement.dataset.matrixOriginalText = nextText;

        if (addToCartButtonContainer) {
          addToCartButtonContainer.dataset.originalAddToCartText = nextText;
        }
      }
    }

    const klaviyoTrigger = this.querySelector(".klaviyo-bis-trigger");
    if (klaviyoTrigger instanceof HTMLButtonElement) {
      klaviyoTrigger.toggleAttribute("hidden", isAvailable);
      if (variantId) {
        klaviyoTrigger.dataset.id = variantId;
      }
    }
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(
    currentElement,
    newElement,
    insertReferenceElement = null,
  ) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement(
        "beforebegin",
        /** @type {Element} */ (newElement.cloneNode(true)),
      );
    }
  }

  /**
   * Finds the matching product form in fetched variant HTML so updates stay scoped to this instance.
   * @param {Document} html
   * @returns {Element | null}
   */
  #getUpdatedProductForm(html) {
    const sectionId = this.dataset.sectionId;
    if (sectionId) {
      const sectionScopedForm = html.querySelector(
        `product-form-component[data-section-id="${sectionId}"]`,
      );
      if (sectionScopedForm) return sectionScopedForm;
    }

    const productId = this.dataset.productId;
    if (productId) {
      const productScopedForm = html.querySelector(
        `product-form-component[data-product-id="${productId}"]`,
      );
      if (productScopedForm) return productScopedForm;
    }

    return html.querySelector("product-form-component");
  }

  /**
   * Keeps the Klaviyo BIS trigger synced with server-rendered variant state.
   * @param {Element} updatedProductForm
   * @param {boolean} isVariantAvailable
   * @param {string} variantId
   */
  #syncKlaviyoBisTrigger(updatedProductForm, isVariantAvailable, variantId) {
    const currentKlaviyoTrigger = this.querySelector(".klaviyo-bis-trigger");
    const newKlaviyoTrigger = updatedProductForm.querySelector(
      ".klaviyo-bis-trigger",
    );

    if (currentKlaviyoTrigger && newKlaviyoTrigger) {
      morph(currentKlaviyoTrigger, newKlaviyoTrigger);
    }

    const klaviyoTrigger = this.querySelector(".klaviyo-bis-trigger");
    if (!(klaviyoTrigger instanceof HTMLButtonElement)) return;

    klaviyoTrigger.dataset.id = variantId;
    klaviyoTrigger.toggleAttribute("hidden", isVariantAvailable);
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? "";
    const {
      addToCartButtonContainer: currentAddToCartButtonContainer,
      acceleratedCheckoutButtonContainer,
    } = this.refs;
    const currentAddToCartButton =
      currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (
      !currentAddToCartButtonContainer ||
      (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)
    )
      return;

    const updatedProductForm = this.#getUpdatedProductForm(
      event.detail.data.html,
    );
    if (!updatedProductForm) return;

    const isVariantAvailable = Boolean(event.detail.resource?.available);
    const variantIdValue = event.detail.resource?.id
      ? event.detail.resource.id.toString()
      : "";

    this.#syncKlaviyoBisTrigger(
      updatedProductForm,
      isVariantAvailable,
      variantIdValue,
    );

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = updatedProductForm.querySelector(
      '[ref="addToCartButton"]',
    );
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      acceleratedCheckoutButtonContainer.setAttribute("hidden", "true");
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100",
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      updatedProductForm.querySelector(
        'quantity-selector-component input[ref="quantityInput"]',
      )
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step,
      );
    }

    const newQuantityRules =
      updatedProductForm.querySelector(".quantity-rules");
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = updatedProductForm.querySelector("price-per-item");
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if (
      (isQuantityRulesChanging || isPricePerItemChanging) &&
      quantitySelector
    ) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = updatedProductForm.querySelector(
        ".product-form-buttons",
      );

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          updatedProductForm.querySelector(
            'quantity-selector-component input[ref="quantityInput"]',
          )
        );

        if (
          this.refs.quantitySelector?.updateConstraints &&
          newQuantityInputElement &&
          currentQuantityValue
        ) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step,
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        [".quantity-label", quantityLabel, quantitySelector],
        [".quantity-rules", quantityRules, this.refs.productFormButtons],
        ["price-per-item", pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(
          currentElement,
          updatedProductForm.querySelector(selector),
          fallback,
        );
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = updatedProductForm.querySelector("volume-pricing");
    this.#morphOrUpdateElement(
      currentVolumePricing,
      newVolumePricing,
      this.refs.productFormButtons,
    );

    this.#syncKlaviyoBisTrigger(
      updatedProductForm,
      isVariantAvailable,
      variantIdValue,
    );

    const hasB2BFeatures =
      quantityRules ||
      newQuantityRules ||
      pricePerItem ||
      newPricePerItem ||
      currentVolumePricing ||
      newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Keep buttons interactive while variant data loads to avoid visible flicker on rapid option changes.
   * @param {Event} event
   */
  #onVariantSelected = (event) => {
    this.#clearLiveRegionText();

    const variantSelectedEvent =
      /** @type {CustomEvent<{ resource?: { available?: boolean, variantId?: string | number } }>} */ (
        event
      );

    const source = event.target;
    if (
      source instanceof HTMLElement &&
      source.dataset.productId &&
      source.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const isAvailable = variantSelectedEvent.detail?.resource?.available;
    if (typeof isAvailable !== "boolean") return;

    const variantId =
      variantSelectedEvent.detail?.resource?.variantId?.toString() || "";
    if (variantId) {
      this.refs.variantId.value = variantId;
    }

    this.#applyOptimisticVariantState(isAvailable, variantId);
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}
